import React from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";

import ViewContainer from "../../../components/ViewContainer";
import DashboardGrid from "../../../components/grid/DashboardGrid";
import Panel from "../../../components/grid/Panel";
import KpiStat from "../../../components/kpi/KpiStat";
import BarList from "../../../components/charts/BarList";
import StackedBar from "../../../components/charts/StackedBar";
import DataTable from "../../../components/DataTable";
import StatusTag from "../../../components/StatusTag";
import CaveatNote from "../../../components/charts/CaveatNote";
import CountUp from "../../../components/CountUp";
import { formatInt } from "../../../utils/format";
import { blueScaleByRank } from "../../../utils/colorScale";

import cohortData from "../../../data/pharma/cohortOverview.json";
import subgroupsData from "../../../data/pharma/subgroups.json";

const statusTone = { open: "warn", review: "info", documented: "neutral" };

// Modulo 2.1 — Cohort · Overview (solo dato de cohorte, sin filtro de subgrupo).
function CohortOverviewView() {
    const { t } = useTranslation();
    const d = cohortData;
    const subgroups = subgroupsData.items;

    const persistenceColors = blueScaleByRank(subgroups.map((s) => s.persistence12mPct));
    const persistenceItems = subgroups.map((s, i) => ({
        label: s.name,
        pct: s.persistence12mPct,
        value: `${s.persistence12mPct}%`,
        color: persistenceColors[i],
    }));

    // StackedBar usa colores CSS directos (no clases sx); se resuelven a hex de marca.
    const brandHex = { primary: "#3248CA", primaryLight: "#5B6FD9", teal: "#ADCAD6", blueSlate: "#3D4F5F", accentMedium: "#FF8239", accent: "#FF5F24" };
    const shareItemsHex = subgroups.map((s) => ({ label: s.name, pct: s.sharePct, value: `${s.n} · ${s.sharePct}%`, color: brandHex[s.color] }));
    const rootCauseItemsHex = d.nonAdherenceRootCause.map((r) => ({ label: r.label, pct: r.pct, value: `${r.pct}%`, color: brandHex[r.color] }));

    const indexTherapyRows = d.indexTherapy.rows.map((r) => ({
        therapy: r.therapy,
        n: formatInt(r.n),
        share: `${r.sharePct}%`,
        disc: `${r.disc12mPct}%`,
    }));

    const openSignalRows = d.openSignals.map((s) => {
        const sg = subgroups.find((x) => x.id === s.subgroupId);
        return {
            signal: s.signal,
            subgroup: sg ? sg.name : t("views.pharmaCohortOverview.wholeCohort"),
            n: formatInt(s.n),
            status: <StatusTag tone={statusTone[s.status]}>{t(`views.pharmaCohortOverview.status.${s.status}`)}</StatusTag>,
        };
    });

    return (
        <ViewContainer title={t("views.pharmaCohortOverview.title")} description={t("views.pharmaCohortOverview.description")}>
            <DashboardGrid gap={2} rowGap={2.5}>
                <Panel span={12} dense>
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 2, sm: 3 }, flexWrap: "wrap" }}>
                        <Kpi first label={t("views.pharmaCohortOverview.kpi.cohortLabel")} value={<CountUp to={d.kpis.cohortN} format={formatInt} />}
                            hint={t("views.pharmaCohortOverview.kpi.cohortHint", { female: d.kpis.femalePct, age: d.kpis.meanAge })} />
                        <Kpi tone="primary" label={t("views.pharmaCohortOverview.kpi.subgroupsLabel")} value={d.kpis.subgroupCount} hint={t("views.pharmaCohortOverview.kpi.subgroupsHint")} />
                        <Kpi label={t("views.pharmaCohortOverview.kpi.persistenceLabel")} value={<CountUp to={d.kpis.persistence12mAvgPct} suffix="%" />} hint={t("views.pharmaCohortOverview.kpi.persistenceHint")} />
                        <Kpi tone="accent" label={t("views.pharmaCohortOverview.kpi.persistenceRangeLabel")}
                            value={`${d.kpis.persistence12mMinPct}–${d.kpis.persistence12mMaxPct}%`} hint={t("views.pharmaCohortOverview.kpi.persistenceRangeHint")} />
                        <Kpi tone="primary" label={t("views.pharmaCohortOverview.kpi.signalsLabel")} value={d.kpis.openSignals} hint={t("views.pharmaCohortOverview.kpi.signalsHint")} />
                        <Kpi label={t("views.pharmaCohortOverview.kpi.windowLabel")} value={d.kpis.cohortFollowUpWindow} hint={t("views.pharmaCohortOverview.kpi.windowHint")} />
                        <Kpi label={t("views.pharmaCohortOverview.kpi.medianLabel")} value={d.kpis.medianFollowUp} hint={t("views.pharmaCohortOverview.kpi.medianHint", { iqr: d.kpis.medianFollowUpIqr })} />
                    </Box>
                </Panel>

                <Panel span={{ xs: 12, md: 6 }} dense title={t("views.pharmaCohortOverview.persistenceBySubgroupTitle")}>
                    <BarList items={persistenceItems} />
                </Panel>

                <Panel span={{ xs: 12, md: 6 }} dense title={t("views.pharmaCohortOverview.nonAdherenceTitle")} subtitle={t("views.pharmaCohortOverview.nonAdherenceHint")}>
                    <StackedBar items={rootCauseItemsHex} />
                </Panel>

                <Panel span={12} dense title={t("views.pharmaCohortOverview.indexTherapyTitle")} subtitle={t("views.pharmaCohortOverview.indexTherapyHint")}>
                    <DataTable
                        columns={[
                            { key: "therapy", label: t("views.pharmaCohortOverview.table.therapy"), width: "1.4fr" },
                            { key: "n", label: t("views.pharmaCohortOverview.table.n"), align: "right", width: "0.6fr" },
                            { key: "share", label: t("views.pharmaCohortOverview.table.share"), align: "right", width: "0.6fr" },
                            { key: "disc", label: t("views.pharmaCohortOverview.table.disc12m"), align: "right", width: "1fr" },
                        ]}
                        rows={indexTherapyRows}
                    />
                    <Box sx={{ mt: 1.5 }}>
                        <CaveatNote>{d.indexTherapy.disclaimer}</CaveatNote>
                    </Box>
                </Panel>

                <Panel span={{ xs: 12, md: 7 }} dense title={t("views.pharmaCohortOverview.openSignalsTitle")} subtitle={t("views.pharmaCohortOverview.openSignalsHint")}>
                    <DataTable
                        columns={[
                            { key: "signal", label: t("views.pharmaCohortOverview.table.signal"), width: "1.3fr" },
                            { key: "subgroup", label: t("views.pharmaCohortOverview.table.subgroup") },
                            { key: "n", label: t("views.pharmaCohortOverview.table.n"), align: "right", width: "0.5fr" },
                            { key: "status", label: t("views.pharmaCohortOverview.table.status"), width: "0.9fr" },
                        ]}
                        rows={openSignalRows}
                    />
                </Panel>

                <Panel span={{ xs: 12, md: 5 }} dense title={t("views.pharmaCohortOverview.shareOfCohortTitle")}>
                    <StackedBar items={shareItemsHex} />
                </Panel>
            </DashboardGrid>
        </ViewContainer>
    );
}

function Kpi({ label, value, hint, tone, first }) {
    return (
        <Box sx={{ flex: { xs: "0 1 auto", sm: "1 1 150px" }, ...(!first && { borderLeft: { sm: "1px solid" }, borderColor: { sm: "divider" }, pl: { sm: 3 } }) }}>
            <KpiStat label={label} value={value} tone={tone} hint={hint} compact />
        </Box>
    );
}

export default CohortOverviewView;
